import { lazy, Suspense } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import ErrorBoundary from "@/components/ErrorBoundary";
import LegacyContractRedirect from "@/components/LegacyContractRedirect";
import { useAuthProvider, AuthProvider, useAuthReady } from "@/hooks/useAuthReady";
import ArrowlinkLoader from "@/components/ArrowlinkLoader";
import ScrollToTop from "@/components/ScrollToTop";
import DemoBanner from "@/components/DemoBanner";
import { useCustomVariables } from "@/hooks/useCustomVariables";
import Home from "./pages/Home";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
// TooltipProvider must wrap routes eagerly because pages (e.g. Leads)
// render <Tooltip> primitives that throw if no provider is in the tree.

/**
 * Wrap dynamic imports so that a one-time stale-chunk failure (typical right
 * after a deploy) gets retried once before bubbling to the ErrorBoundary.
 * This prevents the user from being stuck on the previous page while the new
 * bundle is rolling out.
 */
const lazyWithRetry = <T extends { default: React.ComponentType<any> }>(
  factory: () => Promise<T>,
) =>
  lazy(() =>
    factory().catch((err) => {
      console.warn("Chunk load failed, retrying once:", err?.message);
      return new Promise<T>((resolve, reject) => {
        setTimeout(() => factory().then(resolve, reject), 400);
      });
    }),
  );

// Home is eager-imported above so the landing route paints immediately
// without a Suspense fallback flash. Every other route stays lazy.
const Auth = lazyWithRetry(() => import("./pages/Auth"));
const Onboarding = lazyWithRetry(() => import("./pages/Onboarding"));
const Dashboard = lazyWithRetry(() => import("./pages/Dashboard"));
const Network = lazyWithRetry(() => import("./pages/Network"));
const Leads = lazyWithRetry(() => import("./pages/Leads"));
const AISearch = lazyWithRetry(() => import("./pages/AISearch"));
const Pilot = lazyWithRetry(() => import("./pages/Pilot"));
const Qurrent = lazyWithRetry(() => import("./pages/Qurrent"));
const Templates = lazyWithRetry(() => import("./pages/Templates"));
const AutomationSettings = lazyWithRetry(() => import("./pages/AutomationSettings"));
const Waitlist = lazyWithRetry(() => import("./pages/Waitlist"));
const Admin = lazyWithRetry(() => import("./pages/Admin"));
const Privacy = lazyWithRetry(() => import("./pages/Privacy"));
const Terms = lazyWithRetry(() => import("./pages/Terms"));
const DataDeletion = lazyWithRetry(() => import("./pages/DataDeletion"));
const About = lazyWithRetry(() => import("./pages/About"));
const Careers = lazyWithRetry(() => import("./pages/Careers"));
const Solutions = lazyWithRetry(() => import("./pages/Solutions"));
const Features = lazyWithRetry(() => import("./pages/Features"));
const CaseStudies = lazyWithRetry(() => import("./pages/CaseStudies"));
const FAQ = lazyWithRetry(() => import("./pages/FAQ"));
const BlogColdEmailPersonalization = lazyWithRetry(() => import("./pages/BlogColdEmailPersonalization"));
const Pricing = lazyWithRetry(() => import("./pages/Pricing"));
const Settings = lazyWithRetry(() => import("./pages/Settings"));
const Checkout = lazyWithRetry(() => import("./pages/Checkout"));
const CheckoutSuccess = lazyWithRetry(() => import("./pages/CheckoutSuccess"));
const CheckoutCanceled = lazyWithRetry(() => import("./pages/CheckoutCanceled"));
const Investors = lazyWithRetry(() => import("./pages/Investors"));
const Profile = lazyWithRetry(() => import("./pages/Profile"));
const ContractSign = lazyWithRetry(() => import("./pages/ContractSign"));
const Install = lazyWithRetry(() => import("./pages/Install"));
const EmailTemplatePreview = lazyWithRetry(() => import("./pages/EmailTemplatePreview"));
const EmailPreview = lazyWithRetry(() => import("./pages/EmailPreview"));
const ProductDemo = lazyWithRetry(() => import("./pages/ProductDemo"));
const ProductDemoStatic = lazyWithRetry(() => import("./pages/ProductDemoStatic"));
const Videos = lazyWithRetry(() => import("./pages/Videos"));
const Team = lazyWithRetry(() => import("./pages/Team"));
const PartnerFeedback = lazyWithRetry(() => import("./pages/PartnerFeedback"));
const PartnerFeedbackWidget = lazy(() =>
  import("./components/PartnerFeedbackWidget").catch((error) => {
    console.error("Failed to load PartnerFeedbackWidget chunk:", error);
    return { default: () => null };
  })
);
const Arc = lazy(() => import("./pages/Arc"));
const Arrow = lazy(() => import("./pages/Arrow"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Trust = lazyWithRetry(() => import("./pages/Trust"));
const AuditLog = lazyWithRetry(() => import("./pages/AuditLog"));
const PrivacySettings = lazyWithRetry(() => import("./pages/PrivacySettings"));
const Status = lazyWithRetry(() => import("./pages/Status"));
const LegalDocument = lazyWithRetry(() => import("./pages/LegalDocument"));
const Feedback = lazyWithRetry(() => import("./pages/Feedback"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
      retry: 1,
      gcTime: 30 * 60 * 1000,
    },
  },
});

/**
 * Branded page loader — full-screen ArrowlinkLoader (animated arrow + wordmark).
 * No grey skeleton bars: only the brand mark while the next chunk loads.
 */
const PageLoader = () => <ArrowlinkLoader />;

const AnimatedRoutes = () => {
  const location = useLocation();
  return (
    <div key={location.pathname} className="animate-route-fade-in">
      <Routes location={location}>
        <Route path="/" element={<Home />} />
        <Route path="/auth" element={<Auth />} />
        <Route path="/onboarding" element={<Onboarding />} />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/network" element={<Network />} />
        <Route path="/leads" element={<Leads />} />
        <Route path="/ai-search" element={<AISearch />} />
        <Route path="/pilot" element={<Pilot />} />
        <Route path="/qurrent" element={<Qurrent />} />
        <Route path="/templates" element={<Templates />} />
        <Route path="/automation-settings" element={<AutomationSettings />} />
        <Route path="/waitlist" element={<Waitlist />} />
        <Route path="/admin" element={<Admin />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/data-deletion" element={<DataDeletion />} />
        <Route path="/about" element={<About />} />
        <Route path="/careers" element={<Careers />} />
        <Route path="/solutions" element={<Solutions />} />
        <Route path="/features" element={<Features />} />
        <Route path="/case-studies" element={<CaseStudies />} />
        <Route path="/faq" element={<FAQ />} />
        <Route path="/blog/automated-cold-email-personalization" element={<BlogColdEmailPersonalization />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/checkout" element={<Checkout />} />
        <Route path="/checkout/success" element={<CheckoutSuccess />} />
        <Route path="/checkout/canceled" element={<CheckoutCanceled />} />
        <Route path="/investors" element={<Investors />} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/contract/sign/:id" element={<LegacyContractRedirect />} />
        <Route path="/contract/sign/:token" element={<LegacyContractRedirect />} />
        <Route path="/contract/:id" element={<ContractSign />} />
        <Route path="/c/:code" element={<ContractSign />} />
        <Route path="/c/:slug/:code" element={<ContractSign />} />
        <Route path="/install" element={<Install />} />
        <Route path="/email-template-preview" element={<EmailTemplatePreview />} />
        <Route path="/email-preview" element={<EmailPreview />} />
        <Route path="/product-demo" element={<ProductDemo />} />
        <Route path="/product-demo-static" element={<ProductDemoStatic />} />
        <Route path="/videos" element={<Videos />} />
        <Route path="/team" element={<Team />} />
        <Route path="/partner-feedback" element={<PartnerFeedback />} />
        <Route path="/arc" element={<Arc />} />
        <Route path="/arrow" element={<Arrow />} />
        <Route path="/trust" element={<Trust />} />
        <Route path="/settings/audit" element={<AuditLog />} />
        <Route path="/settings/privacy" element={<PrivacySettings />} />
        <Route path="/status" element={<Status />} />
        <Route path="/legal/:slug" element={<LegalDocument />} />
        <Route path="/feedback" element={<Feedback />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </div>
  );
};

/** Show DemoBanner on every page when user is a demo account */
const GlobalDemoBanner = () => {
  const { user } = useAuthReady();
  if (!user) return null;
  const isDemo = user.email === 'demo@arrowlink.ai' || user.app_metadata?.is_demo;
  if (!isDemo) return null;
  return <DemoBanner />;
};

/** Keeps the runtime custom-variables registry in sync with the database for
 * any signed-in user. Mounted once globally so editors/pickers always see
 * up-to-date variables. */
const CustomVariablesLoader = () => {
  const { user } = useAuthReady();
  if (!user) return null;
  return <CustomVariablesLoaderInner />;
};
const CustomVariablesLoaderInner = () => {
  useCustomVariables();
  return null;
};

const AppRoutes = () => {
  const auth = useAuthProvider();

  return (
    <AuthProvider value={auth}>
      <BrowserRouter>
        <ScrollToTop />
        <ErrorBoundary>
          <main>
            <Suspense fallback={<PageLoader />}>
              <AnimatedRoutes />
            </Suspense>
          </main>

          <GlobalDemoBanner />
          <CustomVariablesLoader />

          <Suspense fallback={null}>
            <PartnerFeedbackWidget />
          </Suspense>
        </ErrorBoundary>
      </BrowserRouter>
    </AuthProvider>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    {/* TooltipProvider wraps routes eagerly so any page can use <Tooltip>.
        Toaster/Sonner remain lazy to keep them out of the critical bundle. */}
    <TooltipProvider>
      <AppRoutes />
      <Toaster />
      <Sonner />
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
